import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import Modal from './Modal';
import SearchableSelect from './SearchableSelect';

const INCLUDE_OPTIONS = [
  { key: 'include_subtasks',    label: 'Subtasks' },
  { key: 'include_links',       label: 'Links' },
  { key: 'include_comments',    label: 'Comments' },
  { key: 'include_attachments', label: 'Attachments' },
  { key: 'include_sprint',      label: 'Sprint Values' },
];

const CloneTaskModal = ({ task, onClose, onSuccess }) => {
  const [summary, setSummary]           = useState('');
  const [assigneeId, setAssigneeId]     = useState(null);
  const [members, setMembers]           = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [cloning, setCloning]           = useState(false);
  const [options, setOptions]           = useState({
    include_subtasks:    false,
    include_links:       false,
    include_comments:    false,
    include_attachments: false,
    include_sprint:      false,
  });

  useEffect(() => {
    setSummary(`CLONE - ${task.title}`);
    api.get(`/projects/${task.project_id}/members`)
      .then(res => setMembers(res.data || []))
      .catch(() => setMembers([]))
      .finally(() => setLoadingMembers(false));
  }, [task.id]); // eslint-disable-line

  const toggleOption = (key) =>
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));

  const handleClone = async () => {
    if (!summary.trim()) return;
    setCloning(true);
    try {
      const res = await api.post(`/tasks/${task.id}/clone`, {
        summary:             summary.trim(),
        assignee_id:         assigneeId || null,
        include_subtasks:    options.include_subtasks,
        include_links:       options.include_links,
        include_comments:    options.include_comments,
        include_attachments: options.include_attachments,
        include_sprint:      options.include_sprint,
      });
      toast.success(`Cloned → ${res.data.task_key}`);
      onClose();
      onSuccess(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to clone task');
    } finally {
      setCloning(false);
    }
  };

  const memberOptions = members.map(m => ({
    value: m.user.id,
    label: `${m.user.first_name} ${m.user.last_name}`,
    email: m.user.email,
  }));

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Clone ${task.task_key || 'Task'}`}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>

        {/* Summary */}
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">
            Summary <span className="required">*</span>
          </label>
          <input
            className="form-input"
            type="text"
            value={summary}
            onChange={e => setSummary(e.target.value)}
            placeholder="Clone summary"
            autoFocus
          />
        </div>

        {/* Assignee */}
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Assignee</label>
          <SearchableSelect
            options={memberOptions}
            value={assigneeId}
            onChange={setAssigneeId}
            placeholder="Unassigned"
            searchPlaceholder="Search members…"
            getOptionDescription={opt => opt.email}
            size="sm"
            clearable
            dropdownWidth={280}
            loading={loadingMembers}
          />
        </div>

        {/* Include options */}
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Include</label>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-1)',
          }}>
            {INCLUDE_OPTIONS.map(({ key, label }) => (
              <label
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  cursor: 'pointer',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-text-primary)',
                  userSelect: 'none',
                }}
              >
                <input
                  type="checkbox"
                  checked={options[key]}
                  onChange={() => toggleOption(key)}
                  style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--color-brand)' }}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            style={{ marginTop: 0 }}
            onClick={onClose}
            disabled={cloning}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleClone}
            disabled={cloning || !summary.trim()}
          >
            {cloning ? 'Cloning…' : 'Clone'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CloneTaskModal;
